import { memo, useCallback, useState } from 'react';

import { type Board, type Library } from '@air/api/types';
import { ChevronRightIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { ItemPickerSidebarBoardRow } from '~/components/ItemPicker/ItemPickerSidebarBoardRow';
import {
  type ItemPickerBoardSelectParams,
  type ItemPickerLibrarySelectParams,
} from '~/components/ItemPicker/itemPickerTypes';
import { useLibraryRootBoards } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type ItemPickerSidebarLibraryRowProps = {
  activeBoardId?: Board['id'];
  isActive: boolean;
  library: Library;
  onSelectBoard: (params: ItemPickerBoardSelectParams) => void;
  onSelectLibrary: (params: ItemPickerLibrarySelectParams) => void;
};

export const ItemPickerSidebarLibraryRow = memo(
  ({ activeBoardId, isActive, library, onSelectBoard, onSelectLibrary }: ItemPickerSidebarLibraryRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { currentWorkspace } = useCurrentWorkspace();

    const { data: rootBoards } = useLibraryRootBoards({
      workspaceId: currentWorkspace?.id,
      libraryId: library.id,
      enabled: isExpanded,
    });

    const handleSelectLibrary = useCallback(() => {
      onSelectLibrary({ libraryId: library.id, label: library.title });
    }, [library.id, library.title, onSelectLibrary]);

    const handleToggleExpanded = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    return (
      <div>
        <div
          className={cn(
            'flex h-10 items-center gap-2 rounded-4 px-2 font-medium text-14 transition-colors',
            isActive
              ? 'ads:bg-subtle bg-gray-3 font-semibold ads:text-primary text-gray-12'
              : 'ads:text-secondary text-gray-11 ads:hover:bg-hover-subtle hover:bg-gray-3',
          )}
          data-testid="ITEM_PICKER_SIDEBAR_LIBRARY_ROW"
        >
          <button
            aria-current={isActive ? 'true' : undefined}
            className="flex min-w-0 grow items-center gap-2 rounded-4 text-left outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
            onClick={handleSelectLibrary}
            type="button"
          >
            <div
              className="flex size-6 shrink-0 items-center justify-center rounded-2 border ads:border-default border-gray-4 text-10"
              style={{ backgroundColor: library.color.backgroundHex, color: library.color.fontHex }}
            >
              {library.icon}
            </div>
            <span className="grow truncate">{library.title}</span>
          </button>
          <button
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${library.title}`}
            className="flex size-5 shrink-0 items-center justify-center rounded-2 ads:text-icon-subtle text-gray-9 outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
            onClick={handleToggleExpanded}
            type="button"
          >
            <ChevronRightIcon className={cn('transition-transform', isExpanded && 'rotate-90')} size="xs" />
          </button>
        </div>

        {isExpanded &&
          rootBoards.map((board) => (
            <ItemPickerSidebarBoardRow
              activeBoardId={activeBoardId}
              board={board}
              depth={0}
              key={board.id}
              libraryId={library.id}
              onSelectBoard={onSelectBoard}
            />
          ))}
      </div>
    );
  },
);

ItemPickerSidebarLibraryRow.displayName = 'ItemPickerSidebarLibraryRow';
